/**
 * Contenu du QR Code d'un titre — composition et relecture.
 *
 * Ce module ne dépend **de rien** : ni `node:crypto`, ni aucune API de
 * plateforme. La PWA Verify doit le charger dans un navigateur, sur un
 * téléphone, hors ligne. C'est la même raison qui a fait sortir la canonisation
 * de `documentaire.ts` — voir `canonisation.ts`.
 *
 * Le QR porte tout ce qu'il faut pour vérifier sans réseau : la version, les
 * données scellées sous leur forme canonique, et la signature. Le vérificateur
 * n'a besoin en plus que de la clé publique, qu'il embarque une fois.
 */
import { CODE_ERREUR, ErreurDelivrance } from '../erreurs.js';
import { canoniserPayload } from './canonisation.js';
/**
 * En-tête de version. Il permettra d'ajouter un champ un jour sans que les
 * anciens QR deviennent illisibles ni les nouveaux mal interprétés.
 */
export const ENTETE_QR = 'SYGAP1';
/**
 * Séparateur des trois segments. Absent de la base64url comme du JSON canonique
 * d'un titre, donc sans ambiguïté de découpage — ce que le contrôle ci-dessous
 * garantit plutôt que de le supposer.
 */
const SEPARATEUR = '|';
/**
 * Compose le contenu à encoder dans le QR Code.
 *
 * @throws {ErreurDelivrance} si une valeur contient le séparateur.
 */
export function composerContenuQr(payload, signature) {
    const canonique = canoniserPayload(payload);
    if (canonique.includes(SEPARATEUR) || signature.includes(SEPARATEUR)) {
        throw new ErreurDelivrance(CODE_ERREUR.DELIVRANCE_PARAMETRE_INVALIDE, `Le séparateur « ${SEPARATEUR} » ne peut pas apparaître dans les données signées.`);
    }
    return `${ENTETE_QR}${SEPARATEUR}${canonique}${SEPARATEUR}${signature}`;
}
/**
 * Décompose un contenu de QR Code.
 *
 * Rend `null` plutôt que de lever : un QR illisible est le cas courant du
 * terrain — étiquette abîmée, scan d'un autre code, photo floue — et non une
 * erreur de programmation.
 *
 * La **forme canonique est rendue telle quelle**, jamais reconstruite depuis un
 * objet : la recanoniser réintroduirait le risque que la canonisation existe
 * précisément pour écarter.
 */
export function analyserContenuQr(contenu) {
    const morceaux = contenu.split(SEPARATEUR);
    const [entete, canonique, signature] = morceaux;
    if (morceaux.length !== 3 || entete !== ENTETE_QR) {
        return null;
    }
    if (canonique === undefined || signature === undefined || signature === '') {
        return null;
    }
    return { canonique, signature };
}
//# sourceMappingURL=qr.js.map